import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';
import { type LessonItem, fetchLessons } from './schedule';

/**
 * «Планування уроків»: a teacher lays a list of topics over the lessons of a term — evenly, or
 * so many lessons a topic — and saves the whole plan at once.
 */
export type SpreadMode = { kind: 'even' } | { kind: 'per'; lessons: number };

/** Topics in order over the lessons in order; cancelled lessons are left out. */
export function spreadTopics(
  lessons: ReadonlyArray<Pick<LessonItem, 'id' | 'status'>>,
  topics: readonly string[],
  mode: SpreadMode,
): Map<string, string | null> {
  const held = lessons.filter((l) => l.status !== 'cancelled');
  const list = topics.map((t) => t.trim()).filter(Boolean);
  const plan = new Map<string, string | null>();
  if (list.length === 0) return plan;
  held.forEach((lesson, i) => {
    let index: number;
    if (mode.kind === 'per') {
      index = Math.floor(i / Math.max(1, mode.lessons));
    } else {
      // the first topics take the extra lessons when they do not divide evenly
      const base = Math.floor(held.length / list.length);
      const extra = held.length % list.length;
      const big = (base + 1) * extra;
      index =
        i < big ? Math.floor(i / (base + 1)) : extra + Math.floor((i - big) / Math.max(1, base));
    }
    // lessons after the last topic keep what they have
    const topic = list[index];
    if (topic !== undefined) plan.set(lesson.id, topic);
  });
  return plan;
}

/** The topics of a course's lessons in order, each once: to copy a plan to a parallel class. */
export function topicsInOrder(lessons: ReadonlyArray<Pick<LessonItem, 'topic'>>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const l of lessons) {
    if (l.topic && !seen.has(l.topic)) {
      seen.add(l.topic);
      out.push(l.topic);
    }
  }
  return out;
}

export function useCourseLessons(schoolId: string, courseId: string, from: string, to: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['lessons', schoolId, { course: courseId }, from, to],
    enabled: courseId !== '' && from !== '',
    queryFn: () => fetchLessons(db, { schoolId, from, to, courseIds: [courseId] }),
  });
}

export function useSetTopics() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (plan: ReadonlyMap<string, string | null>) =>
      must(
        await db.rpc('set_lesson_topics', {
          p_lessons: [...plan.keys()],
          p_topics: [...plan.values()].map((t) => t ?? ''),
        }),
      ),
    onSettled: () => void client.invalidateQueries({ queryKey: ['lessons'] }),
  });
}
